import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CreditCard, Check, X, Sparkles, Zap, Crown, TrendingUp, TrendingDown,
  AlertTriangle, Wallet, Plus, ChevronRight, RefreshCw, Receipt, Brain,
  Store, Film, MessageCircle, BarChart3, Map as MapIcon, Heart, Gift, QrCode,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR } from "../../lib/utils.fmt";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import EmptyState from "../../components/EmptyState";
import { openRazorpayCheckout, fetchRazorpayOrder } from "../../lib/razorpay";

// Icon map for addons
const ADDON_ICON = {
  multi_seller: Store,
  ai_insights: Brain,
  reels: Film,
  whatsapp: MessageCircle,
  advanced_analytics: BarChart3,
  route_optimization: MapIcon,
  demand_forecast: TrendingUp,
  loyalty: Heart,
  referral: Gift,
  qr_bottle: QrCode,
};

const PLAN_ICON = { Starter: Zap, Growth: TrendingUp, Enterprise: Crown };
const PLAN_GRADIENT = {
  Starter: "from-brand-sky/15 to-brand-sky/5",
  Growth: "from-brand-leaf/20 to-brand-leaf/5",
  Enterprise: "from-brand-sand/25 to-brand-sand/5",
};
const PLAN_ACCENT = {
  Starter: "text-brand-sky",
  Growth: "text-brand-leaf",
  Enterprise: "text-brand-sand",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminPlan() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [txns, setTxns] = useState([]);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | yearly
  // Topup
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(0);
  const [topupReason, setTopupReason] = useState("");
  // Plan confirm
  const [pendingPlan, setPendingPlan] = useState(null);
  const [previewCharge, setPreviewCharge] = useState(null);

  const load = async () => {
    const { data } = await api.get("/api/admin/billing/state");
    setState(data);
    setBillingCycle(data.tenant.plan_billing_cycle || "monthly");
    const r = await api.get("/api/admin/billing/transactions");
    setTxns(r.data.transactions || []);
    try {
      const u = await api.get("/api/admin/billing/usage");
      setUsage(u.data);
    } catch { /* trial — no plan */ }
  };
  useEffect(() => { load(); }, []);

  if (!state) {
    return <div className="p-6 text-neutral-500" data-testid="admin-plan-loading">Loading billing…</div>;
  }

  const { tenant, trial, active_plan, plans, addons, wallet } = state;
  const customPlan = tenant?.custom_plan;
  const trialStatus = trial.status;     // paid | trial | trial_expired
  const balance = wallet.balance;
  const isExpired = trialStatus === "trial_expired";
  const isTrial = trialStatus === "trial";
  const isPaid = trialStatus === "paid";

  // Switching plan handler — calculate prorated cost preview first
  const previewSwitch = (plan) => {
    const periodDays = billingCycle === "yearly" ? 365 : 30;
    const newPrice = (billingCycle === "yearly" && plan.yearly_price) ? plan.yearly_price : plan.monthly_price;
    let unusedCredit = 0;
    if (active_plan && tenant.plan_renews_at) {
      const renews = new Date(tenant.plan_renews_at);
      const now = new Date();
      const curCycle = tenant.plan_billing_cycle || "monthly";
      const curPeriod = curCycle === "yearly" ? 365 : 30;
      const curPrice = (curCycle === "yearly" && active_plan.yearly_price) ? active_plan.yearly_price : active_plan.monthly_price;
      const daysLeft = Math.max(0, (renews - now) / 86400000);
      unusedCredit = Math.round(curPrice * (daysLeft / curPeriod) * 100) / 100;
    }
    const net = Math.round((newPrice - unusedCredit) * 100) / 100;
    setPendingPlan(plan);
    setPreviewCharge({ unusedCredit, newPrice, net, periodDays });
  };

  const confirmSwitchPlan = async () => {
    if (!pendingPlan) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/billing/plan", { plan_id: pendingPlan.id, billing_cycle: billingCycle });
      toast.success(
        data.charged > 0
          ? `Plan activated · charged ${formatINR(data.charged)} (${billingCycle})`
          : data.credited > 0
            ? `Plan switched · ${formatINR(data.credited)} credited back to wallet`
            : "Plan switched"
      );
      setPendingPlan(null);
      await load();
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const subscribeAddon = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/billing/addons/${key}`, { billing_cycle: billingCycle });
      toast.success(data.charged > 0 ? `Subscribed · charged ${formatINR(data.charged)} (${billingCycle})` : "Auto-renew turned back on");
      await load();
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const cancelAddon = async (key) => {
    if (!window.confirm("Cancel auto-renew? The addon will stay active until end of paid period, then be removed.")) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/billing/addons/${key}/cancel`);
      toast.success("Auto-renew cancelled");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const payViaRazorpay = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const order = await fetchRazorpayOrder(`/api/admin/wallet/razorpay-order?amount=${amt}`);
      if (order.mode === "mock") {
        // No keys configured — fall back to mock instant credit
        await api.post("/api/admin/wallet/recharge", { amount: amt, method: "mock" });
        toast.success(`Wallet recharged · ${formatINR(amt)} (mock — Razorpay keys not set on platform)`);
        setTopupOpen(false); setTopupAmount(0); setTopupReason("");
        await load();
        return;
      }
      try {
        const resp = await openRazorpayCheckout({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          order_id: order.order_id,
          name: tenant.name,
          description: topupReason || "Plan / addon top-up",
        });
        await api.post("/api/admin/wallet/recharge", {
          amount: amt,
          method: "razorpay",
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_signature: resp.razorpay_signature,
        });
        toast.success(`Wallet recharged · ${formatINR(amt)}`);
        setTopupOpen(false); setTopupAmount(0); setTopupReason("");
        await load();
      } catch (e) {
        if (e?.cancelled) { /* user closed checkout — no toast */ }
        else toast.error(e?.description || e?.response?.data?.detail || "Payment failed");
      }
    } catch (e) { toast.error(e?.description || e.response?.data?.detail || "Razorpay failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 page-enter pb-16" data-testid="admin-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Billing</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">My Plan & Addons</h1>
          <p className="text-sm text-neutral-600 mt-1">Manage your subscription. Plan + addon fees auto-debit from your platform wallet on renewal.</p>
        </div>
        <Button onClick={load} variant="ghost" className="h-10 rounded-2xl" data-testid="reload-billing">
          <RefreshCw className="w-4 h-4 mr-1"/> Refresh
        </Button>
      </div>

      {/* Trial / status banner */}
      {isExpired ? (
        <div className="rounded-3xl bg-brand-clay/10 border-2 border-brand-clay/30 p-5 flex items-start gap-4" data-testid="trial-expired-banner">
          <div className="w-12 h-12 rounded-2xl bg-brand-clay text-white flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6"/>
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-lg text-brand-clay">Trial expired</div>
            <p className="text-sm text-neutral-700 mt-1">
              Your free trial has ended. Activate a plan below to keep your console live for new orders, addons, and integrations.
              Existing customers will continue to receive deliveries.
            </p>
          </div>
        </div>
      ) : isTrial ? (
        <div className="rounded-3xl bg-brand-sand/15 border-2 border-brand-sand/40 p-5 flex items-start gap-4" data-testid="trial-banner">
          <div className="w-12 h-12 rounded-2xl bg-brand-sand text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6"/>
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-lg text-brand-sand">Free trial — {trial.days_left} day{trial.days_left === 1 ? "" : "s"} left</div>
            <p className="text-sm text-neutral-700 mt-1">
              Activate a plan any time to lock in features and unlimited deliveries.
              Trial ends on <b>{fmtDate(tenant.trial_expires_at)}</b>.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl bg-brand-leaf/10 border-2 border-brand-leaf/30 p-5 flex items-start gap-4" data-testid="plan-active-banner">
          <div className="w-12 h-12 rounded-2xl bg-brand-leaf text-white flex items-center justify-center shrink-0">
            <Check className="w-6 h-6"/>
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-lg text-brand-leaf">
              {active_plan?.name} plan · {tenant.plan_billing_cycle === "yearly" ? "Yearly" : "Monthly"} active
            </div>
            <p className="text-sm text-neutral-700 mt-1">
              Next renewal on <b>{fmtDate(tenant.plan_renews_at)}</b> · {trial.days_left} day{trial.days_left === 1 ? "" : "s"} remaining
            </p>
          </div>
        </div>
      )}

      {customPlan && (
        <section className="rounded-3xl bg-gradient-to-br from-brand-clay/15 via-brand-sand/10 to-white border-2 border-brand-clay/30 p-6" data-testid="custom-plan-banner">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-2xl bg-brand-clay text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6"/>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs uppercase tracking-wider font-extrabold text-brand-clay">You're on a Custom Plan</div>
              <div className="font-heading text-2xl font-extrabold tracking-tight mt-0.5">{customPlan.name}</div>
              {customPlan.description && (
                <p className="text-sm text-neutral-700 mt-1">{customPlan.description}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <Stat label="Monthly" value={formatINR(customPlan.monthly_price)}/>
                <Stat label="Yearly" value={formatINR(customPlan.yearly_price)}/>
                <Stat label="Cycle" value={<span className="capitalize">{customPlan.billing_cycle}</span>}/>
                <Stat label="Next Renewal" value={fmtDate(tenant.plan_renews_at)}/>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Stat label="Customers" value={(customPlan.customer_limit || 0) === 0 ? "Unlimited" : customPlan.customer_limit.toLocaleString()}/>
                <Stat label="Deliveries / day" value={(customPlan.delivery_limit_per_day || 0) === 0 ? "Unlimited" : customPlan.delivery_limit_per_day.toLocaleString()}/>
                <Stat label="Storage" value={(customPlan.storage_limit_gb || 0) === 0 ? "Unlimited" : `${customPlan.storage_limit_gb} GB`}/>
              </div>
              {customPlan.included_addons?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-600 mb-1.5">Included Addons</div>
                  <div className="flex flex-wrap gap-1.5">
                    {customPlan.included_addons.map((k) => {
                      const a = addons.find((x) => x.key === k);
                      return (
                        <span key={k} className="px-2.5 py-1 rounded-full bg-brand-leaf/10 text-brand-leaf text-xs font-bold inline-flex items-center gap-1">
                          <Check className="w-3 h-3"/> {a?.name || k}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-neutral-500 mt-4">
                This is a negotiated plan. To change pricing or limits, please contact your DairyFresh account manager.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Wallet card */}
      <div className="rounded-3xl bg-brand-ink text-white p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
           data-testid="billing-wallet-card">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-white/60 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5"/> Platform Wallet
          </div>
          <div className={`font-heading text-4xl font-extrabold mt-1 tracking-tight ${balance < 0 ? "text-brand-clay" : ""}`}
               data-testid="billing-wallet-balance">
            {formatINR(balance)}
          </div>
          <div className="text-xs text-white/60 mt-1">
            Used for OTPs (₹0.30 each), plan & addon renewals. Low balance threshold: {formatINR(wallet.low_balance_threshold || 50)}.
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          {[500, 1000, 5000].map((amt) => (
            <button key={amt} onClick={() => { setTopupAmount(amt); setTopupOpen(true); }}
                    className="h-11 px-4 rounded-2xl bg-white/10 border border-white/20 font-bold text-sm hover:bg-white/20"
                    data-testid={`quick-topup-${amt}`}>
              + {formatINR(amt)}
            </button>
          ))}
          <Button onClick={() => { setTopupAmount(2000); setTopupOpen(true); }}
                  className="h-11 px-4 rounded-2xl bg-white text-brand-ink font-extrabold text-sm hover:bg-white/90"
                  data-testid="open-topup">
            <Plus className="w-4 h-4 mr-1"/> Top up
          </Button>
        </div>
      </div>

      {/* Usage vs plan limits */}
      {usage && usage.stats && usage.stats.length > 0 && (
        <section data-testid="usage-section">
          <h2 className="font-heading text-xl font-extrabold tracking-tight mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5"/> Usage today · {usage.plan_name || "—"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {usage.stats.map((s) => {
              const over = (s.over || 0) > 0;
              const pct = Math.min(100, s.percent || 0);
              return (
                <div key={s.kind} className={`rounded-2xl border p-4 bg-white ${over ? "border-brand-clay" : "border-neutral-200"}`}
                     data-testid={`usage-${s.kind}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">{s.label}</div>
                    <div className={`text-xs font-extrabold ${over ? "text-brand-clay" : "text-neutral-500"}`}>
                      {pct.toFixed(0)}%
                    </div>
                  </div>
                  <div className="font-heading text-2xl font-extrabold mt-1">
                    {typeof s.used === "number" ? (s.kind === "storage_gb" ? s.used.toFixed(2) : s.used.toLocaleString()) : s.used}
                    <span className="text-sm text-neutral-400 font-normal"> / {s.kind === "storage_gb" ? `${s.limit}GB` : s.limit?.toLocaleString()}</span>
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 mt-2 overflow-hidden">
                    <div className={`h-full ${over ? "bg-brand-clay" : pct > 80 ? "bg-brand-sand" : "bg-brand-leaf"}`}
                         style={{ width: `${pct}%` }}/>
                  </div>
                  {over && (
                    <div className="mt-2 text-xs text-brand-clay font-bold" data-testid={`usage-overage-${s.kind}`}>
                      ⚠ {s.kind === "storage_gb" ? `${s.over.toFixed(2)}GB` : s.over} over · {formatINR(s.daily_overage_charge)}/day overage
                    </div>
                  )}
                  {!over && s.rate > 0 && (
                    <div className="mt-2 text-[10px] text-neutral-400">
                      Overage: {formatINR(s.rate)}/unit/day
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Plans */}
      {!customPlan && (
      <section data-testid="plans-section">
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <h2 className="font-heading text-xl font-extrabold tracking-tight">Choose a plan</h2>
          {/* Monthly / Yearly toggle */}
          <div className="inline-flex p-1 rounded-2xl bg-neutral-100" data-testid="billing-cycle-toggle">
            <button onClick={() => setBillingCycle("monthly")}
                    className={`px-4 h-10 rounded-xl text-xs font-bold ${billingCycle === "monthly" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                    data-testid="cycle-monthly">Monthly</button>
            <button onClick={() => setBillingCycle("yearly")}
                    className={`px-4 h-10 rounded-xl text-xs font-bold flex items-center gap-1.5 ${billingCycle === "yearly" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                    data-testid="cycle-yearly">
              Yearly
              <span className="px-1.5 py-0.5 rounded-full bg-brand-leaf text-white text-[9px] font-extrabold uppercase tracking-wider">Save 17%</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const Icon = PLAN_ICON[p.name] || Zap;
            const isCurrent = active_plan?.id === p.id && (tenant.plan_billing_cycle || "monthly") === billingCycle;
            const hasYearly = !!p.yearly_price;
            const priceShown = billingCycle === "yearly" && hasYearly ? p.yearly_price : p.monthly_price;
            const yearlyMonthly = hasYearly ? Math.round(p.yearly_price / 12) : null;
            const yearlySave = hasYearly ? Math.max(0, p.monthly_price * 12 - p.yearly_price) : 0;
            const disabled = billingCycle === "yearly" && !hasYearly;
            return (
              <div key={p.id}
                   className={`relative rounded-3xl border-2 p-6 bg-gradient-to-br ${PLAN_GRADIENT[p.name] || "from-neutral-100 to-white"} ${isCurrent ? "border-brand-leaf" : "border-neutral-200"}`}
                   data-testid={`plan-card-${p.name.toLowerCase()}`}>
                {isCurrent && (
                  <span className="absolute -top-3 left-6 px-3 h-7 leading-7 rounded-full bg-brand-leaf text-white text-xs font-extrabold uppercase tracking-wider">
                    Current
                  </span>
                )}
                <div className={`w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm ${PLAN_ACCENT[p.name] || "text-brand-sky"}`}>
                  <Icon className="w-6 h-6" strokeWidth={2.4}/>
                </div>
                <div className="font-heading text-2xl font-extrabold mt-3">{p.name}</div>
                {p.description && (
                  <div className="text-xs text-neutral-600 mt-1 line-clamp-3 min-h-[2.5rem]">{p.description}</div>
                )}
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="font-heading text-3xl font-extrabold">{formatINR(priceShown)}</span>
                  <span className="text-sm text-neutral-500">/{billingCycle === "yearly" ? "year" : "month"}</span>
                </div>
                {billingCycle === "yearly" && hasYearly && (
                  <div className="text-xs text-brand-leaf font-bold mt-0.5">
                    ≈ {formatINR(yearlyMonthly)}/mo · Save {formatINR(yearlySave)}
                  </div>
                )}
                {disabled && (
                  <div className="text-xs text-neutral-400 font-bold mt-0.5">Yearly not available</div>
                )}
                <ul className="mt-4 space-y-1.5 text-sm">
                  <Bullet>{p.customer_limit.toLocaleString()} customers</Bullet>
                  <Bullet>{p.delivery_limit_per_day.toLocaleString()} deliveries / day</Bullet>
                  <Bullet>{(p.included_modules || []).length} modules included</Bullet>
                </ul>
                <Button onClick={() => previewSwitch(p)}
                        disabled={busy || isCurrent || disabled}
                        className={`w-full h-12 rounded-2xl mt-5 font-extrabold ${isCurrent ? "bg-neutral-200 text-neutral-500 cursor-not-allowed" : "bg-brand-ink text-white"}`}
                        data-testid={`select-plan-${p.name.toLowerCase()}`}>
                  {isCurrent ? "Active" : (active_plan && active_plan.monthly_price > p.monthly_price ? "Downgrade" : (active_plan ? "Upgrade" : "Activate"))}
                </Button>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* Addons (subscribed/included only — tenants no longer self-subscribe;
          contact your account manager to add more modules) */}
      <section data-testid="addons-section">
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <h2 className="font-heading text-xl font-extrabold tracking-tight">Your modules</h2>
          <span className="text-xs text-neutral-500">
            Need more? Contact your <b>DairyFresh</b> account manager.
          </span>
        </div>
        {addons.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-neutral-200" data-testid="no-addons">
            <EmptyState icon={Sparkles} title="No addon modules enabled yet"
                        description="Talk to your account manager to add modules like Loyalty, Referrals, AI Insights and more." />
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {addons.map((a) => {
            const sub = tenant.addon_subs?.[a.key];
            const isSubscribed = !!sub;
            const Icon = ADDON_ICON[a.key] || Sparkles;
            const grantedBySuper = sub?.granted_by_super_admin === true;
            const customIncluded = (customPlan?.included_addons || []).includes(a.key);
            return (
              <div key={a.id} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`addon-card-${a.key}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-brand-leaf/15 text-brand-leaf">
                    <Icon className="w-5 h-5" strokeWidth={2.3}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-base">{a.name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{a.description}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs flex flex-wrap items-center gap-2">
                  {isSubscribed && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-leaf/10 text-brand-leaf font-bold">
                      <Check className="w-3 h-3"/> Active · renews {fmtDate(sub.renews_at)}
                    </span>
                  )}
                  {grantedBySuper && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-sand/15 text-brand-sand font-bold" data-testid={`granted-${a.key}`}>
                      Platform-granted
                    </span>
                  )}
                  {customIncluded && !isSubscribed && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-sky/10 text-brand-sky font-bold">
                      Included in your custom plan
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </section>

      {/* Transactions */}
      <section data-testid="billing-txns-section">
        <h2 className="font-heading text-xl font-extrabold tracking-tight mb-3 flex items-center gap-2">
          <Receipt className="w-5 h-5"/> Recent billing activity
        </h2>
        <div className="rounded-3xl border border-neutral-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
                <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Description</th><th className="text-right px-5 py-3">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {txns.length === 0 ? (
                  <tr><td colSpan="4"><EmptyState icon={Receipt} size="sm" title="No billing activity yet" /></td></tr>
                ) : txns.map((t) => (
                  <tr key={t.id} className="hover:bg-neutral-50" data-testid={`billing-txn-${t.id}`}>
                    <td className="px-5 py-3 text-sm font-mono">{fmtDate(t.created_at)}</td>
                    <td className="px-5 py-3 text-xs uppercase font-bold tracking-wider text-neutral-500">{t.type.replace(/_/g, " ")}</td>
                    <td className="px-5 py-3 text-sm">{t.description || "—"}</td>
                    <td className={`px-5 py-3 text-right font-mono font-bold ${t.amount < 0 ? "text-brand-clay" : "text-brand-leaf"}`}>
                      {t.amount < 0 ? "−" : "+"}{formatINR(Math.abs(t.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Plan-switch confirm dialog */}
      <Dialog open={!!pendingPlan} onOpenChange={(o) => !o && setPendingPlan(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="plan-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Confirm plan</DialogTitle></DialogHeader>
          {pendingPlan && previewCharge && (
            <div className="space-y-3">
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Switching to</div>
                <div className="font-heading text-2xl font-extrabold">{pendingPlan.name}</div>
                <div className="text-sm text-neutral-600">{formatINR(previewCharge.newPrice)}/{billingCycle === "yearly" ? "year" : "month"}{billingCycle === "yearly" ? " · 365-day cycle" : " · 30-day cycle"}</div>
              </div>
              <div className="bg-white rounded-2xl border border-neutral-200 p-4 space-y-2 text-sm">
                <Row label={`${pendingPlan.name} · ${billingCycle}`} value={formatINR(previewCharge.newPrice)}/>
                {previewCharge.unusedCredit > 0 && (
                  <Row label="Prorated credit (current plan)" value={`− ${formatINR(previewCharge.unusedCredit)}`} positive/>
                )}
                <div className="border-t border-neutral-200 pt-2 flex justify-between font-bold">
                  <span>{previewCharge.net >= 0 ? "Charge to wallet" : "Credit to wallet"}</span>
                  <span className={previewCharge.net >= 0 ? "text-brand-clay" : "text-brand-leaf"}>
                    {formatINR(Math.abs(previewCharge.net))}
                  </span>
                </div>
              </div>
              {previewCharge.net > 0 && balance < previewCharge.net && (
                <div className="text-xs bg-brand-clay/10 text-brand-clay rounded-2xl p-3 font-bold" data-testid="low-balance-warning">
                  ⚠ Wallet balance is {formatINR(balance)} — top up by {formatINR(previewCharge.net - balance)} to avoid going negative.
                </div>
              )}
              <Button onClick={confirmSwitchPlan} disabled={busy}
                      className="w-full h-14 rounded-2xl bg-brand-ink text-white font-extrabold"
                      data-testid="confirm-plan-switch">
                {busy ? "Switching…" : `Confirm — ${formatINR(Math.abs(previewCharge.net))}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wallet top-up dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="topup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Top up wallet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Amount (₹)</label>
            <Input type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)}
                   className="h-14 rounded-2xl text-2xl font-heading font-extrabold" data-testid="topup-amount"/>
            <div className="grid grid-cols-4 gap-2">
              {[200, 500, 1000, 5000].map((a) => (
                <button key={a} onClick={() => setTopupAmount(a)}
                        className="h-10 rounded-2xl bg-brand-cream border border-neutral-200 font-bold text-xs"
                        data-testid={`topup-preset-${a}`}>
                  ₹{a}
                </button>
              ))}
            </div>
            <Input value={topupReason} onChange={(e) => setTopupReason(e.target.value)}
                   placeholder="Note (optional)" className="h-12 rounded-2xl" data-testid="topup-reason"/>

            <Button onClick={payViaRazorpay} disabled={busy}
                    className="w-full h-12 rounded-2xl bg-brand-sky text-white font-extrabold"
                    data-testid="pay-razorpay-btn">
              <CreditCard className="w-4 h-4 mr-2"/> Pay {formatINR(parseFloat(topupAmount) || 0)} via Razorpay
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-center gap-2 text-neutral-700">
      <Check className="w-3.5 h-3.5 text-brand-leaf shrink-0"/> {children}
    </li>
  );
}

function Row({ label, value, positive }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-600">{label}</span>
      <span className={`font-mono font-bold ${positive ? "text-brand-leaf" : ""}`}>{value}</span>
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading font-bold text-base mt-0.5">{value}</div>
    </div>
  );
}
